"use client";

import * as React from "react";

import { useBrand, type UseBrandOptions } from "@/hooks/brand";
import type { FetchApiError, StrapiCollectionResponse, StrapiEntity } from "@/lib/strapi";
import type { BrandAttributes } from "@/services/brand";

export type BrandStoreValue = {
  brand: StrapiEntity<BrandAttributes> | null;
  data: StrapiCollectionResponse<BrandAttributes> | null;
  error: FetchApiError | null;
  isLoading: boolean;
  refetch: () => void;
};

const BrandStoreContext = React.createContext<BrandStoreValue | null>(null);

export function BrandProvider({
  children,
  initialData,
  ...options
}: React.PropsWithChildren<UseBrandOptions & { initialData?: StrapiCollectionResponse<BrandAttributes> | null }>): React.JSX.Element {
  const state = useBrand({ ...options, initialData });

  const value: BrandStoreValue = {
    brand: state.brand,
    data: state.data,
    error: state.error,
    isLoading: state.isLoading,
    refetch: state.refetch,
  };

  return <BrandStoreContext.Provider value={value}>{children}</BrandStoreContext.Provider>;
}

export function useBrandStore(): BrandStoreValue {
  const value = React.useContext(BrandStoreContext);
  if (!value) {
    throw new Error("useBrandStore must be used within BrandProvider");
  }
  return value;
}

